import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, Store } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { organizationSelected } from '@/features/tenant/tenantSlice';
import { useBusinessTypesQuery, useCreateOrganizationMutation, useLazyMeQuery } from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { profileLoaded } from '@/features/auth/authSlice';
import { COMMON_CURRENCIES, DEFAULT_CURRENCY } from '@stockpilot/shared';
import { Alert, Button, Card, CardBody, Input, LoadingState, Select } from '@/components/ui';
import { cn } from '@/lib/cn';

const COMMON_COUNTRIES = [
  ['UG', 'Uganda'], ['KE', 'Kenya'], ['TZ', 'Tanzania'], ['RW', 'Rwanda'],
  ['NG', 'Nigeria'], ['GH', 'Ghana'], ['ZA', 'South Africa'], ['US', 'United States'],
  ['GB', 'United Kingdom'], ['IN', 'India'], ['AE', 'United Arab Emirates'],
];

export function OnboardingPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);

  const { data: businessTypes, isLoading: typesLoading } = useBusinessTypesQuery();
  const [createOrganization, { isLoading: creating, error }] = useCreateOrganizationMutation();
  const [fetchMe] = useLazyMeQuery();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '',
    businessType: '',
    currency: DEFAULT_CURRENCY,
    countryCode: 'UG',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    branchName: 'Main Branch',
    warehouseName: 'Main Warehouse',
  });

  const fieldErrors = apiFieldErrors(error);
  const selectedType = useMemo(
    () => businessTypes?.find((type) => type.code === form.businessType),
    [businessTypes, form.businessType],
  );

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]): void =>
    setForm((current) => ({ ...current, [key]: value }));

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    const created = await createOrganization(form).unwrap().catch(() => null);
    if (!created) return;

    // Refresh the membership list so the shell knows about the new business.
    const profile = await fetchMe().unwrap().catch(() => null);
    if (profile) dispatch(profileLoaded(profile));

    dispatch(organizationSelected(created.id));
    navigate('/app/dashboard', { replace: true });
  };

  if (typesLoading) {
    return (
      <div className="grid min-h-screen place-items-center">
        <LoadingState label="Preparing your setup…" />
      </div>
    );
  }

  const steps = ['Business', 'Locale', 'Locations'];

  return (
    <div className="min-h-screen bg-sunken px-4 py-10 sm:px-6">
      <div className="mx-auto w-full max-w-2xl">
        <header className="mb-8 text-center">
          <div className="mx-auto mb-4 grid size-12 place-items-center rounded-xl bg-brand-600 text-lg font-bold text-white">
            S
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome{user?.firstName ? `, ${user.firstName}` : ''}
          </h1>
          <p className="mt-1.5 text-sm text-secondary">
            Set up your business. You can change any of this later in Settings.
          </p>
        </header>

        <ol className="mb-6 flex items-center justify-center gap-2" aria-label="Setup progress">
          {steps.map((label, index) => (
            <li key={label} className="flex items-center gap-2">
              <span
                className={cn(
                  'grid size-7 place-items-center rounded-full text-xs font-medium',
                  index < step
                    ? 'bg-brand-600 text-white'
                    : index === step
                      ? 'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-100'
                      : 'bg-sunken text-muted',
                )}
                aria-current={index === step ? 'step' : undefined}
              >
                {index < step ? <Check className="size-3.5" aria-hidden /> : index + 1}
              </span>
              <span className={cn('text-sm', index === step ? 'font-medium' : 'text-muted')}>
                {label}
              </span>
              {index < steps.length - 1 && <span className="mx-1 h-px w-6 bg-current opacity-20" aria-hidden />}
            </li>
          ))}
        </ol>

        <Card>
          <CardBody>
            <form onSubmit={onSubmit} className="space-y-6" noValidate>
              {error && <Alert tone="danger">{apiErrorMessage(error, 'Could not create the business')}</Alert>}

              {step === 0 && (
                <div className="space-y-5">
                  <Input
                    label="Business name"
                    required
                    icon={Store}
                    placeholder="Kampala Traders Ltd"
                    value={form.name}
                    onChange={(event) => update('name', event.target.value)}
                    error={fieldErrors.name}
                  />

                  <fieldset>
                    <legend className="mb-2 block text-sm font-medium">What kind of business is it?</legend>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {businessTypes?.map((type) => (
                        <button
                          key={type.code}
                          type="button"
                          onClick={() => update('businessType', type.code)}
                          aria-pressed={form.businessType === type.code}
                          className={cn(
                            'rounded-lg border p-3 text-left transition-colors',
                            form.businessType === type.code
                              ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                              : 'border-subtle hover:bg-sunken',
                          )}
                        >
                          <p className="text-sm font-medium">{type.name}</p>
                          <p className="mt-0.5 text-xs text-muted">{type.description}</p>
                        </button>
                      ))}
                    </div>
                    {fieldErrors.businessType && (
                      <p className="mt-2 text-sm text-red-600">{fieldErrors.businessType}</p>
                    )}
                  </fieldset>

                  {selectedType && selectedType.suggestedFeatures.length > 0 && (
                    <Alert tone="info" title="We will switch these on for you">
                      {selectedType.suggestedFeatures.join(', ').replaceAll('_', ' ').toLowerCase()}
                      {' '}— all optional, and editable in Settings.
                    </Alert>
                  )}
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  <Select
                    label="Currency"
                    required
                    value={form.currency}
                    onChange={(event) => update('currency', event.target.value)}
                    hint="Used for every price, cost and report in this business."
                  >
                    {COMMON_CURRENCIES.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </Select>

                  <Select
                    label="Country"
                    required
                    value={form.countryCode}
                    onChange={(event) => update('countryCode', event.target.value)}
                  >
                    {COMMON_COUNTRIES.map(([code, name]) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                  </Select>

                  <Input
                    label="Time zone"
                    value={form.timezone}
                    onChange={(event) => update('timezone', event.target.value)}
                    hint="Detected from your device. Reports and receipts use this."
                  />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <Input
                    label="First branch"
                    required
                    icon={Building2}
                    value={form.branchName}
                    onChange={(event) => update('branchName', event.target.value)}
                    hint="Where you sell from. Add more branches later."
                  />
                  <Input
                    label="First warehouse"
                    required
                    value={form.warehouseName}
                    onChange={(event) => update('warehouseName', event.target.value)}
                    hint="Where stock is held. Every stock movement belongs to a warehouse."
                  />

                  <Alert tone="info" title="What happens next">
                    We create your roles, units of measure, payment methods, document
                    numbering and expense categories — then you can add products and
                    opening stock.
                  </Alert>
                </div>
              )}

              <div className="flex justify-between gap-3 border-t border-subtle pt-5">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep((value) => Math.max(0, value - 1))}
                  disabled={step === 0 || creating}
                >
                  Back
                </Button>

                {step < steps.length - 1 ? (
                  <Button
                    type="button"
                    onClick={() => setStep((value) => value + 1)}
                    disabled={step === 0 && (!form.name.trim() || !form.businessType)}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button type="submit" loading={creating}>Create business</Button>
                )}
              </div>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
